import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JobVacancyPhaseTypeEnumIcons, JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { JobCandidate } from 'src/app/models/job_candidate.model';
import { JobVacancyPhase } from 'src/app/models/job_vacancy.model';

export type JobVacancyPhaseIllumination = 'idle' | 'neutral' | 'low' | 'partial' | 'good' | 'high';

export interface JobVacancyPhaseTrackItem {
  phase_id: number;
  name: string;
  type_label: string;
  icon: string;
  input_count: number;
  output_count: number;
  current_count: number;
  illumination: JobVacancyPhaseIllumination;
  stats_label: string;
  tooltip: string;
}

@Component({
  selector: 'app-job-vacancy-phase-trackline',
  templateUrl: './job-vacancy-phase-trackline.component.html',
  styleUrl: './job-vacancy-phase-trackline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule],
})
export class JobVacancyPhaseTracklineComponent {
  readonly phases = input<JobVacancyPhase[]>([]);
  readonly candidates = input<JobCandidate[]>([]);

  readonly phase_track_items = computed((): JobVacancyPhaseTrackItem[] => {
    const sorted_phases = [...this.phases()].sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order);

    const active_candidates = this.candidates().filter(job_candidate => !this.isJobCandidateDiscarded(job_candidate));
    const total_active_candidates = active_candidates.length;

    return sorted_phases.map(job_vacancy_phase => {
      const input_count = this.countCandidatesEnteredPhase(active_candidates, job_vacancy_phase.id);
      const output_count = this.countCandidatesCompletedPhase(active_candidates, job_vacancy_phase.id);
      const current_count = this.countCandidatesCurrentlyInPhase(active_candidates, job_vacancy_phase.id);

      const input_percentage = total_active_candidates > 0 ? (input_count / total_active_candidates) * 100 : 0;
      const output_percentage = total_active_candidates > 0 ? (output_count / total_active_candidates) * 100 : 0;

      const input_health = this.computeMetricHealth(
        input_count,
        job_vacancy_phase.recommended_min_input_score,
        job_vacancy_phase.recommended_max_input_score
      );
      const output_health = this.computeMetricHealth(
        output_count,
        job_vacancy_phase.recommended_min_output_score,
        job_vacancy_phase.recommended_max_output_score
      );

      const type_label = JobVacancyPhaseTypeEnumLabels[job_vacancy_phase.type] ?? job_vacancy_phase.type;

      return {
        phase_id: job_vacancy_phase.id,
        name: job_vacancy_phase.name,
        type_label,
        icon: JobVacancyPhaseTypeEnumIcons[job_vacancy_phase.type] ?? 'circle',
        input_count,
        output_count,
        current_count,
        illumination: this.combineMetricHealth(input_health, output_health, total_active_candidates),
        stats_label: this.buildStatsLabel(input_count, output_count, current_count),
        tooltip: this.buildTooltip(
          job_vacancy_phase,
          input_count,
          output_count,
          current_count,
          total_active_candidates,
          input_percentage,
          output_percentage
        ),
      };
    });
  });

  private countCandidatesEnteredPhase(active_candidates: JobCandidate[], job_vacancy_phase_id: number): number {
    return active_candidates.filter(job_candidate =>
      job_candidate.job_vacancy_candidate_phase_progress?.some(progress => progress.job_vacancy_phase_id === job_vacancy_phase_id)
    ).length;
  }

  private countCandidatesCompletedPhase(active_candidates: JobCandidate[], job_vacancy_phase_id: number): number {
    return active_candidates.filter(job_candidate =>
      job_candidate.job_vacancy_candidate_phase_progress?.some(
        progress =>
          progress.job_vacancy_phase_id === job_vacancy_phase_id && progress.completed_at !== null && progress.discarded_at === null
      )
    ).length;
  }

  private countCandidatesCurrentlyInPhase(active_candidates: JobCandidate[], job_vacancy_phase_id: number): number {
    return active_candidates.filter(
      job_candidate => job_candidate.active_job_vacancy_candidate_phase_progress?.job_vacancy_phase_id === job_vacancy_phase_id
    ).length;
  }

  private computeMetricHealth(
    actual_count: number,
    recommended_minimum: number | null,
    recommended_maximum: number | null
  ): 'neutral' | 'low' | 'good' | 'high' {
    if (recommended_minimum === null && recommended_maximum === null) {
      return 'neutral';
    }

    const is_below_minimum = recommended_minimum !== null && actual_count < recommended_minimum;
    const is_above_maximum = recommended_maximum !== null && actual_count > recommended_maximum;

    if (is_below_minimum) {
      return 'low';
    }

    if (is_above_maximum) {
      return 'high';
    }

    return 'good';
  }

  private combineMetricHealth(
    input_health: 'neutral' | 'low' | 'good' | 'high',
    output_health: 'neutral' | 'low' | 'good' | 'high',
    total_active_candidates: number
  ): JobVacancyPhaseIllumination {
    if (total_active_candidates === 0) {
      return 'idle';
    }

    if (input_health === 'neutral' && output_health === 'neutral') {
      return 'neutral';
    }

    const health_rank: Record<'low' | 'good' | 'high' | 'neutral', number> = {
      low: 0,
      good: 2,
      high: 1,
      neutral: 1,
    };

    const input_rank = health_rank[input_health];
    const output_rank = health_rank[output_health];
    const combined_rank = Math.min(input_rank, output_rank);

    if (combined_rank >= 2) {
      return 'good';
    }

    if (combined_rank === 1) {
      return 'partial';
    }

    return 'low';
  }

  private buildStatsLabel(input_count: number, output_count: number, current_count: number): string {
    return `${input_count} in · ${output_count} pasan · ${current_count} activos`;
  }

  private buildTooltip(
    job_vacancy_phase: JobVacancyPhase,
    input_count: number,
    output_count: number,
    current_count: number,
    total_active_candidates: number,
    input_percentage: number,
    output_percentage: number
  ): string {
    const input_recommendation = this.formatRecommendationRange(
      job_vacancy_phase.recommended_min_input_score,
      job_vacancy_phase.recommended_max_input_score
    );
    const output_recommendation = this.formatRecommendationRange(
      job_vacancy_phase.recommended_min_output_score,
      job_vacancy_phase.recommended_max_output_score
    );

    return [
      `${job_vacancy_phase.name} (${JobVacancyPhaseTypeEnumLabels[job_vacancy_phase.type]})`,
      `Inscritos en fase: ${input_count} (${input_percentage.toFixed(0)}%) · Recomendado: ${input_recommendation}`,
      `Han pasado: ${output_count} (${output_percentage.toFixed(0)}%) · Recomendado: ${output_recommendation}`,
      `Ahora en fase: ${current_count}`,
      `Candidatos activos en vacante: ${total_active_candidates}`,
    ].join('\n');
  }

  private formatRecommendationRange(minimum: number | null, maximum: number | null): string {
    if (minimum === null && maximum === null) {
      return '—';
    }

    if (minimum !== null && maximum !== null) {
      return `${minimum}–${maximum}`;
    }

    if (minimum !== null) {
      return `≥ ${minimum}`;
    }

    return `≤ ${maximum}`;
  }

  private isJobCandidateDiscarded(job_candidate: JobCandidate): boolean {
    const has_open_progress = job_candidate.job_vacancy_candidate_phase_progress?.some(
      progress => progress.completed_at === null && progress.discarded_at === null
    );

    if (has_open_progress) {
      return false;
    }

    return job_candidate.job_vacancy_candidate_phase_progress?.some(progress => progress.discarded_at !== null) ?? false;
  }
}
